import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Briefcase, Home as HomeIcon, Store, Utensils, ArrowRight, ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// 4 Categories
const CATEGORIES = [
    { id: "office", label: "Office Fit-Out", icon: <Briefcase className="h-6 w-6" /> },
    { id: "home", label: "Home Interiors", icon: <HomeIcon className="h-6 w-6" /> },
    { id: "retail", label: "Retail Branding", icon: <Store className="h-6 w-6" /> },
    { id: "restaurant", label: "Restaurant / Café", icon: <Utensils className="h-6 w-6" /> },
];

// Category-specific step 2 fields
const CATEGORY_FIELDS = {
    office: {
        sizeLabel: "Office size",
        sizes: ["Up to 1,000 sq ft", "1,000–3,000 sq ft", "3,000+ sq ft", "Something Else? / Not Sure"],
        servicesLabel: "Services needed",
        services: [
            "New office fit-out",
            "Office renovation",
            "Workstations & furniture",
            "Reception branding",
            "Glass partitions",
            "Signage & wall branding",
            "Meeting room",
            "MEP / civil coordination",
            "Something Else? / Not Sure",
        ],
    },
    home: {
        sizeLabel: "Home type",
        sizes: ["Apartment", "Villa", "Room only", "Kitchen / wardrobe only", "Something Else? / Not Sure"],
        rooms: ["Studio / 1 BHK", "2 BHK", "3 BHK", "4 BHK+", "Villa"],
        servicesLabel: "Services needed",
        services: [
            "Full home interiors",
            "Kitchen",
            "Wardrobes",
            "Living room",
            "Bedrooms",
            "Lighting",
            "Furniture",
            "Styling",
        ],
    },
    retail: {
        sizeLabel: "Retail size",
        sizes: ["Kiosk / small shop", "Up to 1,000 sq ft", "1,000–3,000 sq ft", "3,000+ sq ft", "Something Else? / Not Sure"],
        servicesLabel: "Services needed",
        services: [
            "Storefront signage",
            "Counter branding",
            "Window graphics",
            "Wall branding",
            "Display zones",
            "Lighting",
            "Retail fit-out",
            "Launch branding",
            "Something Else? / Not Sure",
        ],
    },
    restaurant: {
        sizeLabel: "Space size",
        sizes: ["Small café", "Up to 1,000 sq ft", "1,000–3,000 sq ft", "3,000+ sq ft", "Something Else? / Not Sure"],
        servicesLabel: "Services needed",
        services: [
            "Dining area design",
            "Counter branding",
            "Menu boards",
            "Wall graphics",
            "Lighting",
            "Seating layout",
            "Signage",
            "Launch branding",
        ],
    },
};

// AED starting ranges
function calcRange(category, size, homeType) {
    if (category === "office") {
        if (size === "Up to 1,000 sq ft") return "AED 24,500 – AED 75,000";
        if (size === "1,000–3,000 sq ft") return "AED 75,000 – AED 250,000";
        if (size === "3,000+ sq ft") return "Custom Quote";
        return "Custom Quote";
    }
    if (category === "home") {
        if (homeType === "Villa" || size === "Villa") return "AED 85,000 – AED 300,000+";
        return "AED 20,000 – AED 85,000";
    }
    if (category === "retail") {
        return "AED 12,000 – AED 150,000+";
    }
    if (category === "restaurant") {
        return "AED 18,000 – AED 250,000+";
    }
    return "Custom Quote";
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EstimatorModal({ open, onOpenChange, initialType = "office", onRequestConnect }) {
    const [step, setStep] = useState(1);
    const [category, setCategory] = useState("office");
    const [size, setSize] = useState("");
    const [homeType, setHomeType] = useState("");
    const [services, setServices] = useState([]);
    const [rangeResult, setRangeResult] = useState("");

    useEffect(() => {
        if (open) {
            setStep(1);
            // If parent passed a home-only type (legacy), keep office as default
            const validCats = ["office", "home", "retail", "restaurant"];
            setCategory(validCats.includes(initialType) ? initialType : "office");
            setSize("");
            setHomeType("");
            setServices([]);
            setRangeResult("");
        }
    }, [open, initialType]);

    const fields = CATEGORY_FIELDS[category];

    const toggleService = (s) => {
        setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    };

    const handleCalculate = async () => {
        const range = calcRange(category, size, homeType);
        setRangeResult(range);
        setStep(3);
        try {
            await axios.post(`${API}/estimates`, {
                home_type: category,
                bhk: homeType || null,
                rooms: services,
                vibe: size || null,
                min_price: 0,
                max_price: 0,
            });
        } catch (err) {
            console.warn("Estimate save failed:", err?.message);
        }
    };

    const reset = () => {
        setStep(1);
        setCategory("office");
        setSize("");
        setHomeType("");
        setServices([]);
        setRangeResult("");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[620px] p-0 overflow-hidden rounded-3xl border-0 max-h-[92vh] overflow-y-auto"
                data-testid="estimator-modal"
            >
                <div className="bg-white p-7 sm:p-8">
                    <DialogHeader>
                        <div className="flex items-center justify-between">
                            <DialogTitle className="font-display text-xl font-extrabold">
                                {step === 1 && "What type of space are you planning?"}
                                {step === 2 && "Tell us about your space"}
                                {step === 3 && "Your Estimate Direction"}
                            </DialogTitle>
                            <span className="text-xs font-bold text-black/50">STEP {step} / 3</span>
                        </div>
                        <DialogDescription className="text-sm text-black/60 text-left">
                            {step === 1 && "Pick your project category so we can guide you better."}
                            {step === 2 && "We'll use these details to understand your requirement."}
                            {step === 3 && "An approximate starting range based on your selections."}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Progress bar */}
                    <div className="mt-4 h-1.5 w-full rounded-full bg-black/10 overflow-hidden">
                        <div
                            className="h-full bg-[#CCFF00] transition-all duration-500"
                            style={{ width: `${(step / 3) * 100}%` }}
                        />
                    </div>

                    {/* Step 1 — Category */}
                    {step === 1 && (
                        <div className="mt-6 grid grid-cols-2 gap-3">
                            {CATEGORIES.map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => setCategory(c.id)}
                                    data-testid={`est-cat-${c.id}`}
                                    className={`flex flex-col items-start gap-3 rounded-2xl border-2 p-5 text-left transition-all ${
                                        category === c.id
                                            ? "border-[#CCFF00] bg-[#FFFDE8]"
                                            : "border-black/10 hover:border-black/30"
                                    }`}
                                >
                                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white border border-black/10">
                                        {c.icon}
                                    </div>
                                    <div className="font-display text-sm font-bold">{c.label}</div>
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Step 2 — Details */}
                    {step === 2 && (
                        <div className="mt-6 space-y-6">
                            <div>
                                <div className="text-xs font-bold tracking-wider mb-2">{fields.sizeLabel.toUpperCase()}</div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {fields.sizes.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => setSize(s)}
                                            data-testid={`est-size-${s}`}
                                            className={`rounded-xl border-2 py-2.5 px-3 text-[13px] font-semibold text-left ${
                                                size === s ? "border-[#CCFF00] bg-[#FFFDE8]" : "border-black/10 hover:border-black/30"
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {category === "home" && (
                                <div>
                                    <div className="text-xs font-bold tracking-wider mb-2">APPROX SIZE / ROOMS</div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        {fields.rooms.map((r) => (
                                            <button
                                                key={r}
                                                onClick={() => setHomeType(r)}
                                                className={`rounded-xl border-2 py-2.5 px-3 text-[13px] font-semibold ${
                                                    homeType === r ? "border-[#CCFF00] bg-[#FFFDE8]" : "border-black/10 hover:border-black/30"
                                                }`}
                                            >
                                                {r}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="text-xs font-bold tracking-wider mb-2">{fields.servicesLabel.toUpperCase()}</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {fields.services.map((s) => (
                                        <label
                                            key={s}
                                            className="flex items-center gap-2 rounded-xl border border-black/10 px-3 py-2.5 cursor-pointer hover:border-black/30"
                                        >
                                            <Checkbox
                                                checked={services.includes(s)}
                                                onCheckedChange={() => toggleService(s)}
                                                className="data-[state=checked]:bg-[#CCFF00] data-[state=checked]:text-black data-[state=checked]:border-[#CCFF00]"
                                            />
                                            <span className="text-[13px] font-medium">{s}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3 — Result */}
                    {step === 3 && (
                        <div className="mt-6">
                            <div className="rounded-3xl bg-[#111111] p-7 text-white text-center">
                                <div className="text-xs font-bold tracking-wider text-white/60">ESTIMATED STARTING RANGE</div>
                                <div className="mt-3 font-display text-2xl sm:text-[32px] font-extrabold text-[#CCFF00]" data-testid="estimate-range">
                                    {rangeResult}
                                </div>
                                <div className="mt-4 text-[12px] text-white/60 max-w-md mx-auto leading-relaxed">
                                    This is only an approximate starting range. Final quotation depends on space size, site condition, materials, MEP scope, authority/building requirements and final design.
                                </div>
                            </div>

                            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                                <div className="rounded-xl bg-[#FAFAF5] p-3">
                                    <div className="text-xs text-black/50">Category</div>
                                    <div className="font-bold">{CATEGORIES.find((c) => c.id === category)?.label}</div>
                                </div>
                                <div className="rounded-xl bg-[#FAFAF5] p-3">
                                    <div className="text-xs text-black/50">{fields.sizeLabel}</div>
                                    <div className="font-bold">{size || "—"}</div>
                                </div>
                                {category === "home" && homeType && (
                                    <div className="rounded-xl bg-[#FAFAF5] p-3">
                                        <div className="text-xs text-black/50">Approx size</div>
                                        <div className="font-bold">{homeType}</div>
                                    </div>
                                )}
                                <div className="rounded-xl bg-[#FAFAF5] p-3">
                                    <div className="text-xs text-black/50">Services</div>
                                    <div className="font-bold">{services.length ? `${services.length} selected` : "—"}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Footer actions */}
                    <div className="mt-7 flex items-center justify-between gap-3">
                        {step > 1 && step < 3 && (
                            <button
                                onClick={() => setStep((s) => s - 1)}
                                className="btn-pill btn-outline-dark text-sm"
                                data-testid="est-back-btn"
                            >
                                <ArrowLeft className="h-4 w-4" /> Back
                            </button>
                        )}
                        {step === 3 && (
                            <button onClick={reset} className="inline-flex items-center gap-1.5 text-sm font-semibold text-black/60 hover:text-black" data-testid="est-restart-btn">
                                <RefreshCw className="h-3.5 w-3.5" /> Start Over
                            </button>
                        )}
                        <div className="ml-auto">
                            {step === 1 && (
                                <button
                                    onClick={() => setStep(2)}
                                    data-testid="est-next-btn"
                                    className="btn-pill btn-dark pr-1.5 text-sm"
                                >
                                    <span className="px-1">Next</span>
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#CCFF00] text-black">
                                        <ArrowRight className="h-4 w-4" />
                                    </span>
                                </button>
                            )}
                            {step === 2 && (
                                <button
                                    onClick={handleCalculate}
                                    data-testid="est-calculate-btn"
                                    className="btn-pill btn-lime pr-1.5 text-sm"
                                >
                                    <span className="px-1 font-bold">Calculate Estimate</span>
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-[#CCFF00]">
                                        <ArrowRight className="h-4 w-4" />
                                    </span>
                                </button>
                            )}
                            {step === 3 && (
                                <button
                                    onClick={() => {
                                        if (!onRequestConnect) return;
                                        onRequestConnect({
                                            category: CATEGORIES.find((c) => c.id === category)?.label,
                                            size,
                                            services: services.join(", "),
                                            range: rangeResult,
                                            source: "estimator",
                                        });
                                    }}
                                    data-testid="est-quote-btn"
                                    className="btn-pill btn-lime pr-1.5 text-sm"
                                >
                                    <span className="px-1 font-bold">GET EXACT QUOTE ON WHATSAPP</span>
                                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black text-[#CCFF00]">
                                        <ArrowRight className="h-4 w-4" />
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
